/**
 * Which cure sheet an operator had on screen when a cure was set up.
 *
 * Contract: VIEWER-CONTRACTS.md §3, owned by the cure-sheets session.
 *
 * PURE PROVENANCE. It records what was on screen — never where a number came
 * from. Auto-populate was cancelled, so nothing on the project is derived from
 * the sheet, and the report row reads "Cure sheet referenced" for that reason.
 * The same session deleted an earlier "values overridden" flag specifically
 * because a `false` on every project would read as meaningful. Do not reintroduce
 * that shape of claim here.
 */

/** Written by the desktop at cure setup. OMITTED when the operator chose none. */
export interface CureSheetRef {
  sheetId: string;
  /**
   * Load-bearing, not decoration. A version can be superseded, or have its
   * approval withdrawn, AFTER the cure finishes — so "which document was on
   * screen" is answerable later only because the exact revision is pinned.
   */
  version: number;
}

/**
 * What that version's approval looks like NOW, which is a different question
 * from what the operator followed then.
 *
 * `unknown` is a first-class state, not a placeholder: until the provenance
 * lookup answers, we do not know the standing, and rendering an unknown standing
 * as though it were approved would turn a record of evidence into apparent
 * guidance. That is the failure this type exists to prevent.
 */
export type CureSheetStandingStatus = 'approved' | 'withdrawn' | 'rejected' | 'draft';

/** Local addition: we have not resolved the standing yet. Never sent by the server. */
export type CureSheetStanding = CureSheetStandingStatus | 'unknown';

/** Server standing block — VIEWER-CONTRACTS.md §2c. */
export interface CureSheetStandingBlock {
  status: CureSheetStandingStatus;
  /** Would the field be served this today. */
  currentlyServable: boolean;
  withdrawn: boolean;
  since?: string;
  sheetArchived?: boolean;
  /**
   * A rendered line such as "approval withdrawn 2026-08-14".
   *
   * RENDER IT VERBATIM. The server composes it because it is the side that knows
   * the transition history, and because "withdrawn" and "never approved" both
   * sit at `draft` today — only that history separates them. Re-deriving the
   * wording here from `status` alone would mislabel a never-approved document as
   * withdrawn, asserting a review that never happened.
   */
  summary: string;
}

/** Resolved record from the provenance lookup — §2c. */
export interface CureSheetProvenance {
  sheetId: string;
  version: number;
  /** The display name. Absent until resolved — never invent one from the id. */
  brandName?: string;
  fileName?: string;
  uploadedAt?: string;
  standing: CureSheetStandingBlock;
}

/** A project carries this only when the operator chose a sheet. */
export interface HasCureSheetRef {
  cureSheetRef?: CureSheetRef;
}

/**
 * Is there anything to show?
 *
 * ABSENT IS NORMAL AND IS NOT AN ERROR. The ref is operator-chosen and optional,
 * so most projects will not have one — especially every project that predates
 * the feature. A project with no ref renders NOTHING: no row, no empty value, no
 * "none". An empty field would read as "no sheet was followed", which is a claim
 * about the cure that this data cannot support.
 */
export function hasCureSheetRef(project: HasCureSheetRef | null | undefined): boolean {
  const ref = project?.cureSheetRef;
  return !!ref && typeof ref.sheetId === 'string' && ref.sheetId.length > 0 && ref.version > 0;
}

/**
 * The provenance line: what was followed, and how that document stands today.
 *
 * EVIDENCE MUST NEVER READ AS GUIDANCE — the ruling this was built to. A cure
 * reviewed months later is often reviewed *because* something went wrong, and a
 * withdrawn sheet is exactly the one under scrutiny. Showing "Followed: Sheet A
 * v2" alone would let a withdrawn document read as current instruction.
 *
 * So the standing is part of the line, not a badge somewhere else that can be
 * missed, and an UNRESOLVED standing says so rather than staying silent.
 */
export function describeCureSheetRef(
  ref: CureSheetRef,
  resolved?: CureSheetProvenance | null
): string {
  // Never invent a name from the id: a truncated GUID reads like a title.
  const label = resolved?.brandName
    ? `${resolved.brandName} v${ref.version}`
    : `sheet ${ref.sheetId} v${ref.version}`;

  if (!resolved) {
    // Not "Followed: X" on its own. Silence about standing is indistinguishable
    // from "approved" to a reader, and that is the exact confusion barred here.
    return `Followed: ${label} — current approval status unavailable`;
  }

  // THE SERVER'S WORDING, VERBATIM. It is the side that can tell "withdrawn"
  // from "never approved" — both sit at `draft`, separated only by transition
  // history this client does not have. Composing our own line from `status`
  // would mislabel a never-approved document as withdrawn.
  const summary = (resolved.standing?.summary ?? '').trim();
  if (resolved.standing?.status === 'approved' && !summary) {
    return `Followed: ${label}`;
  }
  return summary ? `Followed: ${label} — ${summary}` : `Followed: ${label}`;
}

/**
 * Should the line be visually marked as needing attention?
 *
 * Driven by `currentlyServable` rather than by `status`, because that is the
 * single question a reader has: would the field be given this document today?
 * A withdrawn, rejected or draft version all answer no, and each for a different
 * reason the server's `summary` already states.
 */
export function standingNeedsAttention(standing?: CureSheetStandingBlock | null): boolean {
  if (!standing) return false;
  return standing.currentlyServable === false;
}
